import {
    Block,
    CHILD_OBJECT_INVALID,
    CHILD_TYPE_INVALID,
    type Change,
    type NodeType,
    PARENT_TYPE_INVALID,
    Schema,
    type SchemaNormalizeContextForViolation,
    type SchemaNormalizeViolationFn,
    Text,
} from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import { BLOCK_CODE, BLOCK_CODE_LINE, INLINE_ANNOTATION, INLINE_EXPRESSION } from '../types';
import { createCodeBlock, isPlainTextSyntax } from './utils';

/**
 * Schema to validate code blocks.
 */
export const schema = Schema.create({
    validations: [splitNewLines],
    blocks: {
        [BLOCK_CODE]: {
            nodes: [{ types: [BLOCK_CODE_LINE] }],
            data: {
                syntax: (syntax?: string) =>
                    syntax === undefined ||
                    (typeof syntax === 'string' && !isPlainTextSyntax(syntax)),
                title: (title?: string) => title === undefined || typeof title === 'string',
                overflow: (overflow?: string) =>
                    overflow === undefined || overflow === 'scroll' || overflow === 'wrap',
                lineNumbers: (lineNumbers?: boolean) =>
                    lineNumbers === undefined || typeof lineNumbers === 'boolean',
                // @deprecated Per-block width is no longer consumed by the editor or renderer
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
                expandable: (expandable?: boolean) =>
                    expandable === undefined || typeof expandable === 'boolean',
                collapsedLineCount: (collapsedLineCount?: number) =>
                    collapsedLineCount === undefined ||
                    (typeof collapsedLineCount === 'number' &&
                        Number.isInteger(collapsedLineCount) &&
                        collapsedLineCount > 0),
            },
            // Cast: the normalizer is authored for the narrow `Block` node it is
            // registered against, but the `normalize` slot is typed for the wide
            // `NodeType`. The schema only ever calls it with the matching block.
            normalize: Schema.createNormalize<Block>({
                [CHILD_TYPE_INVALID]: (change, context) => onlyLine(change, context),
            }) as SchemaNormalizeViolationFn<NodeType>,
        },
        [BLOCK_CODE_LINE]: {
            parent: { types: [BLOCK_CODE] },
            nodes: [
                {
                    objects: ['text', 'inline'],
                    types: [undefined, 'text', INLINE_ANNOTATION, INLINE_EXPRESSION],
                    min: 1,
                },
            ],
            data: {
                highlighted: (highlighted) =>
                    highlighted === undefined || typeof highlighted === 'boolean',
            },
            marks: [],
            // Cast: the normalizer is authored for the narrow `Block` node it is
            // registered against, but the `normalize` slot is typed for the wide
            // `NodeType`. The schema only ever calls it with the matching block.
            normalize: Schema.createNormalize<Block>({
                [PARENT_TYPE_INVALID]: (change, context) => noOrphanLine(change, context),
                [CHILD_OBJECT_INVALID]: (change, context) => onlyTextInCode(change, context),
                [CHILD_TYPE_INVALID]: (change, context) => onlyTextInCode(change, context),
            }) as SchemaNormalizeViolationFn<NodeType>,
        },
    },
});

/**
 * Return a list of group of nodes matching the given filter.
 */
function getSuccessiveNodes(
    nodes: List<NodeType>,
    match: (node: NodeType) => boolean
): List<List<NodeType>> {
    const nonLines = nodes.takeUntil(match);
    const afterNonLines = nodes.skip(nonLines.size);

    if (afterNonLines.isEmpty()) {
        return List.create();
    }

    const firstGroup = afterNonLines.takeWhile(match);
    const restOfNodes = afterNonLines.skip(firstGroup.size);

    return List.create([firstGroup]).concat(getSuccessiveNodes(restOfNodes, match));
}

/**
 * A rule that ensure code blocks only contain lines of code, and no marks
 */
function onlyLine(
    change: Change,
    context: SchemaNormalizeContextForViolation<Block, typeof CHILD_TYPE_INVALID>
) {
    // @ts-expect-error
    const nonLineGroups = getSuccessiveNodes(context.node.nodes, (n) => n.type !== BLOCK_CODE_LINE);

    nonLineGroups
        .filter((group) => !group.isEmpty())
        .forEach((nonLineGroup) => {
            // Convert text to code lines
            const text = nonLineGroup.reduce(
                // @ts-expect-error
                (accu, n, index) => accu + (Block.isBlock(n) && index > 0 ? '\n' : '') + n.text,
                ''
            );
            const codeLines = createCodeBlock(text).nodes;

            // Insert them in place of the invalid node
            const first = nonLineGroup.first();
            const parent = context.node; // change.value.document.getParent(first.key);
            // @ts-expect-error
            const invalidNodeIndex = parent.getChildIndex(first.key);

            codeLines.forEach((codeLine, index) => {
                change.insertNodeByKey(parent.key, invalidNodeIndex + index, codeLine, {
                    normalize: false,
                });
            });

            // Remove the block
            // @ts-expect-error
            nonLineGroup.forEach((n) => change.removeNodeByKey(n.key, { normalize: false }));
        });

    return change;
}

/**
 * A rule that ensure code lines only contain text
 */
function onlyTextInCode(
    change: Change,
    context: SchemaNormalizeContextForViolation<Block, typeof CHILD_OBJECT_INVALID>
): Change | undefined {
    const { node } = context;

    node.nodes.forEach((child) => {
        if (child.object === 'inline') {
            change.unwrapInlineByKey(child.key, { normalize: false });
        } else if (child.object === 'block') {
            change.unwrapNodeByKey(child.key, { normalize: false });
        }
    });

    return undefined;
}

/**
 * A rule that ensure code lines are always children
 * of a code block.
 */
function noOrphanLine(
    change: Change,
    context: SchemaNormalizeContextForViolation<Block, typeof PARENT_TYPE_INVALID>
): void {
    const { parent } = context;

    // @ts-expect-error
    const linesGroup = getSuccessiveNodes(parent.nodes, (n) => n.type === BLOCK_CODE_LINE);

    linesGroup.forEach((group) => {
        const container = Block.create({ type: BLOCK_CODE, nodes: [] });
        // @ts-expect-error
        const firstLineIndex = parent.nodes.indexOf(group.first());

        change.insertNodeByKey(parent.key, firstLineIndex, container, {
            normalize: false,
        });

        group.forEach((line, index) =>
            // @ts-expect-error
            change.moveNodeByKey(line.key, container.key, index, {
                normalize: false,
            })
        );
    });
}

/**
 * A rule that prevent the use of newline chars in code block's texts
 */
function splitNewLines(node: NodeType): undefined | ((change: Change) => void) {
    if (node.object !== 'block' || node.type !== BLOCK_CODE) {
        return undefined;
    }

    const result: Array<{
        line: Block;
        newLines: Block[];
    }> = [];

    node.nodes.forEach((line) => {
        if (line.object !== 'block' || line.type !== BLOCK_CODE_LINE) {
            return;
        }

        const texts = line.text.split(/\r?\n/);
        if (texts.length === 1) {
            return;
        }

        result.push({
            line,
            newLines: texts.map((text) =>
                Block.create({
                    type: BLOCK_CODE_LINE,
                    data: line.data,
                    nodes: [Text.create(text)],
                })
            ),
        });
    });

    if (result.length === 0) {
        return undefined;
    }

    return (change: Change) => {
        result.forEach(({ line, newLines }) => {
            const codeBlock = change.value.document.getNode(node.key) as Block;
            const index = codeBlock.getChildIndex(line.key);

            change.removeNodeByKey(line.key, { normalize: false });
            newLines.forEach((newLine, newLineIndex) =>
                change.insertNodeByKey(codeBlock.key, index + newLineIndex, newLine, {
                    normalize: false,
                })
            );
        });
    };
}
